/**
 * Customer measurement-reference presentation.
 * No site, room, or condition strings live here.
 * Raw scan/report/control ids stay in auditIdentity; customer face uses windows/status.
 */

export const CUSTOMER_REFERENCE_LABELS = {
  initialBaseline: 'Initial baseline',
  currentControl: 'Current Control',
  currentControlReference: 'Current Control reference',
  currentScan: 'Current Scan',
  controlInForce: 'Control in force',
  comparisonReference: 'Comparison reference',
} as const;

export type MeasurementIdentityView = {
  established: boolean;
  label: string;
  identity: string;
  auditIdentity: string;
  windowLabel: string;
  status: string;
};

export type MeasurementReferencesView = {
  initialBaseline: MeasurementIdentityView;
  currentControl: MeasurementIdentityView;
  currentControlReference: MeasurementIdentityView;
  currentScan: MeasurementIdentityView;
  selection: 'A' | 'B' | 'C';
  primary: MeasurementIdentityView;
  secondary: MeasurementIdentityView | null;
  comparablePairLabel: string;
  controlInForceLabel: string;
  initialBaselineEligible: boolean;
  controlReferenceEligible: boolean;
  preControlBaselineUnavailable: boolean;
  historicalControlEffectiveness: string;
  historyHref: string;
};

const NOT_ESTABLISHED = 'Not established';
const UNAVAILABLE = 'Unavailable';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asText(value: unknown): string {
  return String(value || '').trim();
}

function looksLikeRawId(value: string): boolean {
  if (!value) return false;
  return /^(scan-|report-|control-|action-|rev )/i.test(value) || /\brev \d+\b/i.test(value);
}

function identityView(raw: unknown, fallbackLabel: string): MeasurementIdentityView {
  const blob = asRecord(raw);
  const established = blob.established === true;
  const windowLabel = asText(blob.window_label);
  const customerDisplay = asText(blob.customer_display);
  const status = asText(blob.status)
    || (established ? 'Established' : NOT_ESTABLISHED);
  const unavailable = status === 'unavailable_for_this_control_period'
    || asText(blob.code) === 'PRE_CONTROL_BASELINE_UNAVAILABLE'
    || status.toLowerCase() === 'unavailable';
  const auditIdentity = asText(blob.identity_display);
  const customerFacing = unavailable
    ? UNAVAILABLE
    : (customerDisplay
      || windowLabel
      || (established ? 'Established' : NOT_ESTABLISHED));
  return {
    established,
    label: asText(blob.customer_label) || fallbackLabel,
    identity: looksLikeRawId(customerFacing) ? (windowLabel || status || NOT_ESTABLISHED) : customerFacing,
    auditIdentity: unavailable ? UNAVAILABLE : auditIdentity,
    windowLabel: unavailable ? UNAVAILABLE : windowLabel,
    status: unavailable ? UNAVAILABLE : status,
  };
}

export function presentMeasurementReferences(
  raw: unknown,
  opts?: { historyHref?: string | null },
): MeasurementReferencesView {
  const blob = asRecord(raw);
  const initialBaseline = identityView(blob.initial_baseline, CUSTOMER_REFERENCE_LABELS.initialBaseline);
  const currentControl = identityView(blob.current_control, CUSTOMER_REFERENCE_LABELS.currentControl);
  const currentControlReference = identityView(
    blob.current_control_reference,
    CUSTOMER_REFERENCE_LABELS.currentControlReference,
  );
  const currentScan = identityView(blob.current_scan, CUSTOMER_REFERENCE_LABELS.currentScan);
  const selectionRaw = asText(blob.selection);
  const selection: 'A' | 'B' | 'C' = selectionRaw === 'A' || selectionRaw === 'B' ? selectionRaw : 'C';
  const primary = identityView(
    blob.primary_reference,
    currentControlReference.established
      ? CUSTOMER_REFERENCE_LABELS.currentControlReference
      : CUSTOMER_REFERENCE_LABELS.initialBaseline,
  );
  const secondaryEstablished = asRecord(blob.secondary_reference).established === true;
  const secondary = secondaryEstablished
    ? identityView(blob.secondary_reference, CUSTOMER_REFERENCE_LABELS.initialBaseline)
    : null;
  const left = primary.established ? (primary.windowLabel || primary.identity) : NOT_ESTABLISHED;
  const right = currentScan.windowLabel || currentScan.identity || NOT_ESTABLISHED;
  const currentControlNotInForce = currentControl.status.toLowerCase() === 'not_in_force'
    || currentControl.identity.toLowerCase() === 'none currently in force';
  const controlDisplay = currentControlNotInForce
    ? 'None currently in force'
    : currentControl.established || currentControl.status.toLowerCase().includes('recorded')
    ? (currentControl.identity === NOT_ESTABLISHED ? 'In force' : currentControl.identity)
    : NOT_ESTABLISHED;
  const unavailable = blob.pre_control_baseline_unavailable === true
    || initialBaseline.status === UNAVAILABLE;
  return {
    initialBaseline,
    currentControl: {
      ...currentControl,
      identity: looksLikeRawId(currentControl.identity) ? controlDisplay : currentControl.identity,
    },
    currentControlReference,
    currentScan,
    selection,
    primary,
    secondary,
    comparablePairLabel: unavailable
      ? `Historical Control result NOT COMPARABLE · ${left} vs ${right}`
      : `${left} vs ${right}`,
    controlInForceLabel: looksLikeRawId(controlDisplay) ? 'In force' : controlDisplay,
    initialBaselineEligible: unavailable ? false : blob.initial_baseline_eligible === true,
    controlReferenceEligible: blob.control_reference_eligible === true,
    preControlBaselineUnavailable: unavailable,
    historicalControlEffectiveness: unavailable
      ? 'NOT COMPARABLE'
      : asText(blob.historical_control_effectiveness),
    historyHref: asText(opts?.historyHref) || '/acceptance/reports',
  };
}
